import { useEffect, useState } from "react";
import {
  CheckCircle2,
  PackageCheck,
  ShoppingBag,
  Printer,
  Copy,
  Check,
  Truck,
  MapPin,
  CreditCard,
  ArrowRight,
  ShieldCheck,
  Clock,
} from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";

import { ClearCartOnSuccess } from "@/components/cart/clear-cart-on-success";
import { apiGet } from "@/services/api";
import { formatPrice } from "@/lib/utils";
import type { OrderWithItems } from "@/types/prisma";

export function CheckoutSuccessPage() {
  const params = useSearchParams();
  const orderId = params.get("orderId") ?? undefined;

  const [order, setOrder] = useState<OrderWithItems | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(orderId));
  const [copied, setCopied] = useState<boolean>(false);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    let active = true;
    apiGet<{ order: OrderWithItems }>(`/api/orders/${orderId}`)
      .then((data) => {
        if (active && data?.order) {
          setOrder(data.order);
        }
      })
      .catch(() => {
        // Fall back gracefully to generic success if individual order fetch fails
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [orderId]);

  const handleCopyId = () => {
    if (!orderId) return;
    void navigator.clipboard.writeText(orderId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
      <ClearCartOnSuccess />

      {/* Main Confirmation Card */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-white/10 dark:bg-slate-900">
        {/* Header Hero Banner */}
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 px-6 py-10 text-center text-white sm:px-12">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-md">
            <CheckCircle2 className="h-12 w-12 text-white" />
          </div>
          <h1 className="mt-5 text-2xl font-black tracking-tight sm:text-3xl">
            Thank you, your order has been placed!
          </h1>
          <p className="mx-auto mt-2 max-w-xl text-sm text-emerald-50 sm:text-base">
            We have received your order and are currently preparing it for shipment.
            A confirmation notification has been sent to your account.
          </p>

          {orderId && (
            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-xs font-semibold backdrop-blur-md sm:text-sm">
              <span className="text-emerald-100">Order ID:</span>
              <span className="font-mono tracking-wider text-white">{orderId}</span>
              <button
                type="button"
                onClick={handleCopyId}
                aria-label="Copy Order ID"
                className="ml-1 rounded p-1 text-white hover:bg-white/20 transition-colors"
                title="Copy Order ID"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-emerald-200" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            </div>
          )}
        </div>

        {/* Content Body */}
        <div className="p-6 sm:p-8">
          {/* Action Buttons Top */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-6 dark:border-white/10">
            <div className="flex flex-wrap items-center gap-3">
              <Link
                href="/dashboard/orders"
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-amazon-gold px-5 text-sm font-bold text-slate-950 transition-transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <PackageCheck className="h-4 w-4" />
                View in orders
              </Link>
              <Link
                href="/search"
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 text-sm font-bold text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                <ShoppingBag className="h-4 w-4" />
                Continue shopping
              </Link>
            </div>

            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 px-4 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
            >
              <Printer className="h-3.5 w-3.5" />
              Print receipt
            </button>
          </div>

          {/* Loading Skeleton */}
          {loading && (
            <div className="mt-8 space-y-6 animate-pulse">
              <div className="h-28 rounded-xl bg-slate-100 dark:bg-white/5" />
              <div className="h-48 rounded-xl bg-slate-100 dark:bg-white/5" />
            </div>
          )}

          {/* Loaded Order Information */}
          {!loading && order && (
            <div className="mt-8 space-y-8">
              {/* Delivery & Payment Info Grid */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {/* Delivery Estimate */}
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 dark:border-white/10 dark:bg-slate-800/50">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    <Truck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                    Delivery method
                  </div>
                  <p className="mt-3 font-bold text-slate-900 capitalize dark:text-white">
                    {order.deliveryMethod || "Standard"} delivery
                  </p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    Estimated in 2 - 3 business days
                  </p>
                </div>

                {/* Shipping Address */}
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 dark:border-white/10 dark:bg-slate-800/50">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    <MapPin className="h-4 w-4 text-amazon-orange" />
                    Shipping to
                  </div>
                  <p className="mt-3 font-bold text-slate-900 dark:text-white">
                    {order.shippingFullName || "Recipient"}
                  </p>
                  <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                    {order.shippingLine1}
                    {order.shippingLine2 ? `, ${order.shippingLine2}` : ""}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {order.shippingCity}, {order.shippingCountry}
                  </p>
                  {order.shippingPhone && (
                    <p className="mt-1 text-xs font-mono text-slate-500 dark:text-slate-400">
                      {order.shippingPhone}
                    </p>
                  )}
                </div>

                {/* Payment Details */}
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 dark:border-white/10 dark:bg-slate-800/50">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    <CreditCard className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    Payment method
                  </div>
                  <p className="mt-3 font-bold text-slate-900 capitalize dark:text-white">
                    {order.paymentProvider === "COD" ? "Cash on Delivery" : order.paymentProvider}
                  </p>
                  <div className="mt-2 inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                    {order.paymentStatus === "PAID" ? "Payment Completed" : "Payment On Delivery"}
                  </div>
                </div>
              </div>

              {/* Order Items Section */}
              <div className="rounded-xl border border-slate-200 p-5 dark:border-white/10">
                <h2 className="text-base font-bold text-slate-900 dark:text-white">
                  Items in this order ({order.items?.length ?? 0})
                </h2>
                <div className="mt-4 divide-y divide-slate-100 dark:divide-white/5">
                  {order.items?.map((item) => (
                    <div key={item.id} className="flex items-center gap-4 py-3.5 first:pt-0 last:pb-0">
                      <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-slate-800">
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.title}
                            className="h-full w-full object-contain p-1"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-xs text-slate-400">
                            MOVA
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <Link
                          href={`/products/${item.productId}`}
                          className="line-clamp-1 text-sm font-semibold text-slate-900 hover:text-amazon-orange dark:text-white dark:hover:text-amazon-orange"
                        >
                          {item.title}
                        </Link>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          Qty: <span className="font-bold text-slate-700 dark:text-slate-300">{item.quantity}</span>
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-900 dark:text-white">
                          {formatPrice(item.price * item.quantity)}
                        </p>
                        {item.quantity > 1 && (
                          <p className="text-xs text-slate-400">
                            {formatPrice(item.price)} each
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary Pricing Breakdown */}
              <div className="flex justify-end">
                <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-slate-50/50 p-5 dark:border-white/10 dark:bg-slate-800/40">
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">Summary</h3>
                  <dl className="mt-3 space-y-2 text-xs">
                    <div className="flex justify-between text-slate-600 dark:text-slate-300">
                      <dt>Subtotal</dt>
                      <dd className="font-semibold">{formatPrice(order.subtotal)}</dd>
                    </div>
                    {order.discount > 0 && (
                      <div className="flex justify-between text-emerald-600 dark:text-emerald-400">
                        <dt>Discount</dt>
                        <dd className="font-semibold">-{formatPrice(order.discount)}</dd>
                      </div>
                    )}
                    <div className="flex justify-between text-slate-600 dark:text-slate-300">
                      <dt>Shipping</dt>
                      <dd className="font-semibold">
                        {order.shipping === 0 ? "FREE" : formatPrice(order.shipping)}
                      </dd>
                    </div>
                    <div className="flex justify-between border-t border-slate-200 pt-2.5 text-sm font-black text-slate-950 dark:border-white/10 dark:text-white">
                      <dt>Grand Total</dt>
                      <dd className="text-amazon-orange">{formatPrice(order.total)}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          )}

          {/* Guarantee Badges Footer */}
          <div className="mt-10 grid grid-cols-1 gap-4 border-t border-slate-100 pt-8 sm:grid-cols-3 dark:border-white/10">
            <div className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-300">
              <Truck className="h-6 w-6 text-amazon-gold flex-shrink-0" />
              <div>
                <p className="font-bold text-slate-900 dark:text-white">Reliable Ethiopia Delivery</p>
                <p className="text-slate-500 dark:text-slate-400">Delivered directly to your doorstep or pickup station.</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-300">
              <ShieldCheck className="h-6 w-6 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="font-bold text-slate-900 dark:text-white">100% Purchase Protection</p>
                <p className="text-slate-500 dark:text-slate-400">Authentic products and guaranteed quality on all orders.</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-300">
              <PackageCheck className="h-6 w-6 text-blue-600 flex-shrink-0" />
              <div>
                <p className="font-bold text-slate-900 dark:text-white">Real-Time Tracking</p>
                <p className="text-slate-500 dark:text-slate-400">Follow every stage of your order directly from your dashboard.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
